import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api, getToken } from '../api';
import { objectPronoun } from '../utils';
import { formatConstitution } from '../lib/constitutionLabels';

const TIER_GRADIENTS = {
  'Soul Mate':  'linear-gradient(160deg, #1a1408 0%, #2a1f06 40%, #0d0d0d 100%)',
  'Top Match':  'linear-gradient(160deg, #0e0c18 0%, #1a1630 40%, #0d0d0d 100%)',
  'Match':      'linear-gradient(160deg, #080e0a 0%, #0f1e12 40%, #0d0d0d 100%)',
  default:      'linear-gradient(160deg, #0d0d0d 0%, #141414 100%)',
};

const TIER_CLASS = {
  'Soul Mate': 'tier-soul-mate',
  'Top Match': 'tier-top-match',
  'Match': 'tier-match',
};

export default function MatchDetail() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const match = state?.match;
  const [feedback, setFeedback] = useState(null);
  const [isMutual, setIsMutual] = useState(false);
  const [openingChat, setOpeningChat] = useState(false);
  const [summary, setSummary] = useState('');
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [isPlus, setIsPlus] = useState(false);
  const [showMessageForm, setShowMessageForm] = useState(false);
  const [preMessage, setPreMessage] = useState('');
  const [messageSent, setMessageSent] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [markingConnected, setMarkingConnected] = useState(false);
  const [deeper, setDeeper] = useState(null);
  const [deeperLoading, setDeeperLoading] = useState(false);
  const swipeStartY = useRef(null);
  const swipeStartX = useRef(null);

  useEffect(() => {
    if (!match || !getToken()) return;
    api.get('/profile').then(p => setIsPlus(p.subscription_tier === 'plus')).catch(() => {});
    api.get('/mutual-matches')
      .then(data => {
        const mutual = data.some(m => m.matched_user_id === match.user_id);
        setIsMutual(mutual);
        if (mutual) {
          api.get(`/matches/${match.user_id}/connection-status`)
            .then(d => setIsConnected(d.is_connected))
            .catch(() => {});
        }
      })
      .catch(() => {});
    api.get(`/match-summary/${match.user_id}`)
      .then(data => { setSummary(data.summary); setSummaryLoading(false); })
      .catch(() => setSummaryLoading(false));
  }, [match]);

  if (!match) {
    return (
      <div style={{ height: '100vh', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <button className="btn btn-muted" style={{ maxWidth: 200 }} onClick={() => navigate('/matches')}>
          ← Back
        </button>
      </div>
    );
  }

  const gradient = TIER_GRADIENTS[match.tier] || TIER_GRADIENTS.default;
  const tierClass = TIER_CLASS[match.tier] || 'tier-match';

  const handlePhotoTouchStart = (e) => {
    swipeStartY.current = e.touches[0].clientY;
    swipeStartX.current = e.touches[0].clientX;
  };

  const handlePhotoTouchEnd = (e) => {
    if (swipeStartY.current === null) return;
    const deltaY = e.changedTouches[0].clientY - swipeStartY.current;
    const deltaX = Math.abs(e.changedTouches[0].clientX - swipeStartX.current);
    swipeStartY.current = null;
    swipeStartX.current = null;
    if (deltaY > 60 && deltaX < 40) {
      navigate(-1);
    }
  };

  const handleFeedback = async (val) => {
    setFeedback(val);
    try {
      const result = await api.post('/matches/feedback', {
        target_user_id: match.user_id,
        feedback: val,
      });
      if (result.is_mutual) setIsMutual(true);
    } catch {}
  };

  const handleOpenChat = async () => {
    setOpeningChat(true);
    try {
      const { channel_id } = await api.post('/chat/channel', { target_user_id: match.user_id });
      navigate(`/chat/${channel_id}`, { state: { name: match.name } });
    } catch {
      setOpeningChat(false);
    }
  };

  return (
    <div className="match-detail-screen">
      {/* Photo */}
      <div className="match-detail-photo" onTouchStart={handlePhotoTouchStart} onTouchEnd={handlePhotoTouchEnd}>
        <div style={{ position: 'absolute', inset: 0, background: gradient }} />
        <div style={{
          position: 'absolute',
          top: '50%', left: '50%',
          transform: 'translate(-50%, -60%)',
          fontFamily: 'var(--font-display)',
          fontSize: 260,
          fontWeight: 300,
          opacity: 0.07,
          color: '#fff',
          lineHeight: 1,
          pointerEvents: 'none',
          userSelect: 'none',
        }}>
          {match.name?.[0] || '?'}
        </div>
        {match.photo_url && (
          <img
            src={match.photo_url}
            alt={match.name}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}
        <div className="match-detail-overlay" />

        <button className="match-detail-back" onClick={() => navigate(-1)}>
          ← Back
        </button>

        <div className="match-detail-photo-info">
          <div className={`tier-badge ${tierClass}`}>
            <div className="tier-dot" />
            {match.tier}
          </div>
          <div className="match-name" style={{ fontSize: 36, marginTop: 8 }}>{match.name}</div>
          {match.location && (
            <div className="match-location-text">{match.location}</div>
          )}
          {match.dosha_type && (
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.58)', fontWeight: 300, marginTop: 4, letterSpacing: 0.5 }}>
              {formatConstitution(match.dosha_type)}
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="match-detail-body">
        <div className="match-detail-section">
          <div className="match-detail-section-label">Why you match</div>
          {summaryLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 4 }}>
              <span className="spinner" style={{ width: 14, height: 14 }} />
              <span style={{ fontSize: 12, color: 'var(--very-muted)' }}>Reading your compatibility...</span>
            </div>
          ) : (
            <div>
              {(summary || '').split('\n').map(l => l.replace(/^•\s*/, '').trim()).filter(Boolean).map((line, i, arr) => (
                <div key={i}>
                  <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', padding: '10px 0' }}>
                    <div style={{ width: 4, height: 4, borderRadius: '50%', background: 'var(--gold)', marginTop: 9, flexShrink: 0, opacity: 0.7 }} />
                    <span className="match-detail-summary">{line}</span>
                  </div>
                  {i < arr.length - 1 && (
                    <div style={{ height: '0.5px', background: 'rgba(255,255,255,0.08)' }} />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {match.reasons?.length > 0 && (
          <>
            <div className="divider" />
            <div className="match-detail-section">
              <div className="match-detail-section-label">Reasons</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 6 }}>
                {match.reasons.map((reason, i) => (
                  <div key={i} style={{
                    fontSize: 14,
                    color: 'rgba(255,255,255,0.58)',
                    fontWeight: 300,
                    lineHeight: 1.55,
                  }}>
                    {reason}
                  </div>
                ))}
              </div>
              <div style={{
                marginTop: 14,
                fontSize: 12,
                color: 'var(--gold)',
                fontFamily: 'var(--font-display)',
                fontStyle: 'italic',
                opacity: 0.65,
              }}>
                — Paula
              </div>
            </div>
          </>
        )}

        {match.bio && (
          <>
            <div className="divider" />
            <div className="match-detail-section">
              <div className="match-detail-section-label">About {objectPronoun(match.gender)}</div>
              <div className="match-detail-bio">{match.bio}</div>
            </div>
          </>
        )}

        {match.breakdown && (
          <>
            <div className="divider" />
            <div className="match-detail-section">
              <div className="match-detail-section-label">Compatibility breakdown</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {Object.entries(match.breakdown)
                  .filter(([key, val]) => !(key === 'intimacy_bonus' && !val))
                  .map(([key, val]) => (
                  <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 11, color: 'var(--muted)', letterSpacing: 1.5, textTransform: 'uppercase' }}>
                      {key.replace(/_/g, ' ')}
                    </span>
                    <span style={{ fontSize: 13, color: 'var(--text)', fontWeight: 300 }}>
                      {typeof val === 'number' ? `${Math.round(val)}` : val}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        <div className="divider" />

        <div className="match-detail-section">
          <div className="match-detail-section-label">Your response</div>
          <div className="feedback-actions">
            <button
              className={`feedback-btn${feedback === 'not_for_me' ? ' active' : ''}`}
              onClick={() => handleFeedback('not_for_me')}
            >
              Not for me
            </button>
            <button
              className={`feedback-btn${feedback === 'intrigued' ? ' active' : ''}`}
              onClick={() => handleFeedback('intrigued')}
            >
              Intrigued
            </button>
            <button
              className={`feedback-btn interested${feedback === 'interested' ? ' active' : ''}`}
              onClick={() => handleFeedback('interested')}
            >
              Interested
            </button>
          </div>
          {feedback && !isMutual && (
            <div style={{ marginTop: 16, fontSize: 12, color: 'var(--very-muted)', textAlign: 'center' }}>
              Noted. We'll use this to improve your next set.
            </div>
          )}

          {/* Pre-match message (Matchmaker+ only) */}
          {!isMutual && !messageSent && (
            <div style={{ marginTop: 20 }}>
              {!showMessageForm ? (
                <button
                  onClick={() => isPlus ? setShowMessageForm(true) : null}
                  style={{
                    width: '100%', padding: '11px 0',
                    background: 'transparent',
                    border: `0.5px solid ${isPlus ? 'rgba(201,168,76,0.25)' : 'rgba(255,255,255,0.08)'}`,
                    color: isPlus ? 'rgba(201,168,76,0.7)' : 'var(--very-muted)',
                    fontSize: 12, letterSpacing: 0.5,
                    cursor: isPlus ? 'pointer' : 'default',
                  }}
                >
                  {isPlus ? `Message ${match.name?.split(' ')[0] || 'them'} before matching →` : '🔒 Message before matching — Matchmaker+ only'}
                </button>
              ) : (
                <div style={{ border: '0.5px solid rgba(201,168,76,0.2)', padding: 14 }}>
                  <div style={{ fontSize: 11, color: 'var(--very-muted)', letterSpacing: 1, marginBottom: 10 }}>
                    SEND A NOTE TO {(match.name || 'THEM').toUpperCase()}
                  </div>
                  <textarea
                    value={preMessage}
                    onChange={e => setPreMessage(e.target.value.slice(0, 200))}
                    placeholder={`Say hello to ${match.name?.split(' ')[0] || 'them'}...`}
                    rows={3}
                    style={{
                      width: '100%', background: 'rgba(255,255,255,0.04)',
                      border: '0.5px solid rgba(255,255,255,0.1)',
                      color: 'var(--text)', fontFamily: 'var(--font-ui)',
                      fontSize: 13, fontWeight: 300, padding: '10px 12px',
                      resize: 'none', outline: 'none', lineHeight: 1.5,
                      boxSizing: 'border-box',
                    }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
                    <span style={{ fontSize: 11, color: 'var(--very-muted)' }}>{preMessage.length}/200</span>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={() => setShowMessageForm(false)}
                        style={{ padding: '8px 14px', background: 'transparent', border: '0.5px solid rgba(255,255,255,0.1)', color: 'var(--very-muted)', fontSize: 12, cursor: 'pointer' }}
                      >
                        Cancel
                      </button>
                      <button
                        disabled={!preMessage.trim() || sendingMessage}
                        onClick={async () => {
                          if (!preMessage.trim()) return;
                          setSendingMessage(true);
                          try {
                            await api.post('/pre-match-messages', { to_user_id: match.user_id, message: preMessage.trim() });
                            setMessageSent(true);
                            setShowMessageForm(false);
                          } catch {
                            // silently fail
                          } finally {
                            setSendingMessage(false);
                          }
                        }}
                        style={{
                          padding: '8px 16px',
                          background: preMessage.trim() ? 'rgba(201,168,76,0.12)' : 'transparent',
                          border: `0.5px solid ${preMessage.trim() ? 'rgba(201,168,76,0.4)' : 'rgba(255,255,255,0.1)'}`,
                          color: preMessage.trim() ? 'var(--gold)' : 'var(--very-muted)',
                          fontSize: 12, cursor: preMessage.trim() ? 'pointer' : 'default',
                        }}
                      >
                        {sendingMessage ? 'Sending...' : 'Send'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          {messageSent && (
            <div style={{ marginTop: 16, fontSize: 12, color: 'rgba(201,168,76,0.6)', textAlign: 'center' }}>
              Message sent — they'll see it in their requests.
            </div>
          )}
        </div>

        {isMutual && (
          <>
            <div className="divider" />
            <div className="match-detail-section">
              <button
                className="mutual-match-btn"
                onClick={handleOpenChat}
                disabled={openingChat}
              >
                <span className="mutual-match-dot" />
                {openingChat ? 'Opening...' : 'You both said yes — say hello →'}
              </button>

              {/* Connected + Go Deeper */}
              <div style={{ marginTop: 16 }}>
                {!isConnected ? (
                  <button
                    onClick={async () => {
                      setMarkingConnected(true);
                      try {
                        await api.post(`/matches/${match.user_id}/connected`, {});
                        setIsConnected(true);
                      } catch {}
                      setMarkingConnected(false);
                    }}
                    disabled={markingConnected}
                    style={{
                      background: 'none', border: '0.5px solid rgba(255,255,255,0.1)',
                      color: 'var(--very-muted)', fontSize: 12, padding: '8px 16px',
                      cursor: 'pointer', letterSpacing: 0.5, width: '100%',
                    }}
                  >
                    {markingConnected ? 'Saving…' : 'We connected →'}
                  </button>
                ) : (
                  <>
                    <button
                      onClick={async () => {
                        if (deeper) return;
                        setDeeperLoading(true);
                        try {
                          const data = await api.get(`/matches/${match.user_id}/deeper`);
                          setDeeper(data.summary);
                        } catch (err) {
                          setDeeper(err.message || 'Could not load deeper read.');
                        }
                        setDeeperLoading(false);
                      }}
                      disabled={deeperLoading}
                      style={{
                        background: deeper ? 'none' : 'rgba(201,168,76,0.06)',
                        border: '0.5px solid rgba(201,168,76,0.3)',
                        color: deeper ? 'var(--very-muted)' : 'var(--gold)',
                        fontSize: 12, padding: '8px 16px',
                        cursor: deeperLoading || deeper ? 'default' : 'pointer',
                        letterSpacing: 0.5, width: '100%',
                      }}
                    >
                      {deeperLoading ? 'Reading…' : deeper ? 'Deep read ✓' : 'Go deeper →'}
                    </button>
                    {deeper && (
                      <div style={{
                        marginTop: 12, padding: '14px 16px',
                        background: 'rgba(201,168,76,0.04)',
                        border: '0.5px solid rgba(201,168,76,0.15)',
                        fontSize: 13, color: 'rgba(255,255,255,0.55)',
                        lineHeight: 1.75, fontWeight: 300,
                      }}>
                        <div style={{ fontSize: 10, color: 'rgba(201,168,76,0.5)', letterSpacing: 1, marginBottom: 8 }}>DEEPER READ</div>
                        {deeper}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
